import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import axios from 'axios';
import {
  LayoutDashboard,
  FileText,
  Users,
  Building2,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Settings,
  KeyRound,
  Mail,
  Palette
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5956ea83-268b-47c4-a704-9204051c5002/artifacts/w25p61k6_0splendor%20vettoriale%202020%20S%20filigrana.jpg";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = ({ children }) => {
  const { user, logout, isAdmin, isGestore, isAdminOrGestore } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [draftCount, setDraftCount] = useState(0);

  // Fetch draft count
  useEffect(() => {
    const fetchDraftCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/reports/drafts/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDraftCount(res.data.count || 0);
      } catch (err) {
        console.error('Error fetching draft count:', err);
      }
    };
    
    fetchDraftCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchDraftCount, 30000);
    return () => clearInterval(interval);
  }, [location.pathname]); // Refresh when navigating

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/reports', label: 'Rapporti', icon: FileText },
  ];

  // Admin and Gestore can see Employees and Sites
  if (isAdminOrGestore()) {
    navItems.push(
      { path: '/employees', label: 'Dipendenti', icon: Users },
      { path: '/sites', label: 'Cantieri', icon: Building2 }
    );
  }

  // Only Admin can see Settings
  if (isAdmin()) {
    navItems.push(
      { path: '/settings', label: 'Impostazioni', icon: Settings },
      { path: '/pdf-settings', label: 'Personalizza PDF', icon: Palette },
      { path: '/email-monitor', label: 'Monitor Email', icon: Mail }
    );
  }

  // All users can change password
  navItems.push({ path: '/change-password', label: 'Cambia Password', icon: KeyRound });

  const isActive = (path) => location.pathname === path;

  const getRoleBadge = () => {
    if (isAdmin()) return { class: 'bg-purple-500/30 text-purple-200', label: 'Admin' };
    if (isGestore()) return { class: 'bg-blue-500/30 text-blue-200', label: 'Gestore' };
    return { class: 'bg-white/20 text-white/80', label: 'Dipendente' };
  };

  const roleBadge = getRoleBadge();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex sidebar w-64 h-screen fixed left-0 top-0 flex-col" data-testid="sidebar">
        {/* Logo */}
        <div className="p-4 border-b border-white/10 bg-white/10">
          <img
            src={LOGO_URL}
            alt="Splendor Sud"
            className="h-14 w-auto mx-auto"
          />
          <p className="text-white text-xs mt-2 text-center font-medium">Rapporti Lavoro Straordinario</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
              data-testid={`nav-${item.path.slice(1)}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
              {item.path === '/reports' && draftCount > 0 && (
                <span className="ml-auto mr-2 bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                  {draftCount}
                </span>
              )}
              {isActive(item.path) && <ChevronRight className="w-4 h-4 ml-auto" />}
            </Link>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-white/10 bg-white/5">
          <div className="mb-3">
            <p className="font-medium text-white text-sm">{user?.name}</p>
            <p className="text-xs text-white/60 font-mono">{user?.matricola}</p>
            <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs ${roleBadge.class}`}>
              {roleBadge.label}
            </span>
          </div>
          <Button
            variant="destructive"
            className="w-full justify-center bg-red-600 hover:bg-red-700 text-white"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#1B6B93] text-white px-4 py-3 flex items-center justify-between">
        <img
          src={LOGO_URL}
          alt="Splendor Sud"
          className="h-10 w-auto"
        />
        <Button
          variant="ghost"
          size="sm"
          className="text-white"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="absolute top-14 left-0 right-0 bg-white shadow-lg" onClick={e => e.stopPropagation()}>
            <nav className="p-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-[#1B6B93] text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  {item.path === '/reports' && draftCount > 0 && (
                    <span className="ml-auto bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {draftCount}
                    </span>
                  )}
                </Link>
              ))}
              <button
                className="flex items-center gap-3 px-4 py-3 rounded-lg w-full text-red-600 hover:bg-red-50 font-medium"
                onClick={handleLogout}
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="md:ml-64 min-h-screen pt-16 md:pt-0">
        <div className="p-4 md:p-6 lg:p-8 pb-20 md:pb-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
